import {
  baseFormattedDate,
  useLazyQuery,
  useTranslation,
} from '@gimlite/common';
import {
  Col,
  Drawer,
  Row,
  Tab,
  TabGroup,
  Text,
  Zone,
  ZoneArea,
} from '@gimlite/ui-v2';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Query, QuerySiteArgs } from '../../../../client/graphql';
import { siteGql } from '../gql/site.gql';
import { DateTime } from 'luxon';

const alarmsMapper = {
  'coin.Acceptor.AutoTests': 'Echec des tests automatiques monnayeur',
  'coin.Acceptor.Controller': "Erreur de l'initialisation monnayeur",
  'coin.Acceptor.Generic': 'Monnayeur indisponible',
  'coin.Acceptor.Open': 'Monnayeur ouvert',
  'coin.Escrow.AutoTests': 'Echec des tests automatiques caisse intermédiaire',
  'coin.Escrow.Controller': "Erreur de l'initialisation caisse intermédiaire",
  'coin.Escrow.Generic': 'Caisse intermédiaire indisponible',
  'coin.Escrow.RefundOrCash': 'Erreur caisse intermédiaire',
  'coin.SafeBox.AESKey': 'Clé de sécurité de la tirelire non programmée',
  'coin.SafeBox.AlmostFull': 'Tirelire presque pleine',
  'coin.SafeBox.AutoTests': 'Echec des tests automatiques coffre',
  'coin.SafeBox.CantAuthenticate': 'Clé de sécurité du coffre invalide',
  'coin.SafeBox.Controller': "Erreur de l'initialisation coffre",
  'coin.SafeBox.Full': 'Tirelire pleine',
  'coin.SafeBox.Generic': 'Carte de la porte coffre défectueuse',
  'coin.SafeBox.OpenOrMissingBox': 'Coffre ouvert ou tirelire absente',
  'coin.Shutter.OpenOrClose': 'Problème du shutter monnaie',
  'configsync.sync': 'Erreur de synchro de la config',
  'databatch.send': "Erreur d'envoi de fragment synchrone",
  'databatch.sync': 'Erreur de remontée de fragments',
  'door.OpenDoor': 'Porte principale ouverte',
  'eft.Internal.BankAccounts': 'Contrat bancaire indisponible',
  'eft.Internal.ComInterface': 'Interface lecteur bancaire indisponible',
  'eft.Internal.GPIO': 'Erreur initialisation lecteur bancaire (GPIO)',
  'eft.Internal.Service': "Echec de l'initialisation du paiement bancaire",
  'eft.Internal.Store': 'Echec du chargement des trx bancaires',
  'eft.Terminal.FailedToSleep':
    'Echec de la mise en veille du lecteur bancaire',
  'eft.Terminal.NotPresent': 'Lecteur bancaire non détecté',
  'eft.Terminal.NotReady': 'Lecteur bancaire indisponible',
  'hal.Power.RemainingSources': 'Echec de mise en veille périphériques',
  'mcu.Battery.Empty': 'Batterie vide',
  'mcu.Charger': 'Erreur recharge batterie',
  'mcu.DCDC': 'Erreur convertisseur de tension (DCDC)',
  'mcu.Internal': 'Erreur microcontroleur carte mère',
  'mcu.Measurement': "Erreur mesure d'energie",
  'mcu.MPU': 'Erreur communication avec le MPU',
  'mcu.PAC': "Erreur d'acquisition de données sur le bus I2C",
  'mcu.PowerSupply.Forced': 'Sélection de la charge batterie forcée',
  'mcu.Scheduler': 'Erreur de scheduler energie',
  'modem.Config': 'Problème de configuration modem',
  'modem.Generic': 'Problème générique modem',
  'modem.Hardware': 'Problème hardware du modem',
  'modem.Network': 'Echec connexion au réseau',
  'modem.Sim': 'Problème carte SIM',
  'modem.Starting': 'Echec du démarrage modem',
  'modem.Stopping': 'Echec mise en veille du modem',
  'NFC.Device.Error': 'Erreur de communication avec le chip NFC',
  'NFC.Keys.Missing': 'Clés de décryptage NFC manquantes',
  'opto.Communication': 'Module détecteur de présence indisponible',
  'opto.MotionDetection': 'Erreur détecteur de présence',
  'opto.PresenceSensorInit': 'Erreur initialisation détecteur de présence',
  'opto.PresenceSensorObstructed': 'Détecteur de présence obstrué',
  'PAY.Failed': 'Paiement échoué',
  'pm.Suspend.Blocked': 'Mise en veille profonde désactivée',
  'POS.ConditionsNotMet': 'Echec de la génération du reçu ou ticket',
  'POS.FailedToPrint': "Erreur d'impression du ticket",
  'POS.FailedToUpload': 'Erreur de transfert vers le serveur',
  'POS.InternalError': 'Erreur interne inconnue',
  'POS.UserCancelled': "Transaction annulée par l'utilisateur",
  'POS.UserInactivity': "Inactivité de l'utilisateur",
  'printer.Device.Communication': "Erreur de communication avec l'imprimante",
  'printer.Device.Generic': 'Imprimante défectueuse',
  'printer.Device.Printing': 'Papier vide ou bourrage',
  'printer.Device.Unsupported': 'Imprimante non supportée',
  'printer.LowPaper': 'Papier presque vide',
  'system.BGTasks.DataSync': 'Echec de la synchronisation serveur',
  'system.BGTasks.Update': 'Echec de la mise à jour firmware',
  'system.DisabledOutOfOrder': 'Gestion du hors service indisponible',
  'system.InDeliveryMode': 'La machine est en mode installation',
  'system.OutOfOrder': 'Machine hors service',
  'system.OutOfOrder.Manual': 'Mise hors service sur site',
  'system.OutOfOrder.Action': 'Mise hors service à distance',
  'system.OutOfOrder.Scheduled': 'Mise hors service programmée',
  'system.Services.NoInternetConnection': 'Connection internet indisponible',
  'system.Services.NoMeansOfPayment': 'Moyens de paiement indisponibles',
  'system.Services.NoPrinting': 'Imprimante indisponible',
  'system.Services.NoTariff': 'Aucun tarif défini',
  'actions.Internal.Store': 'Erreur chargement des actions à distance',
  'actions.Internal.Databatch': 'Erreur initialisation des actions à distance',
  'system.Config.Invalid': 'Erreur chargement des fichiers de configuration',
} as const satisfies Record<string, string>;

type Alarm = keyof typeof alarmsMapper;

export const EquipmentDetails = observer(
  ({
    id,
    isOpen,
    onClose,
  }: {
    id?: string | null;
    isOpen: boolean;
    onClose: () => void;
  }) => {
    const { lang } = useTranslation();
    const [menu, setMenu] = useState<
      'history_alarms' | 'info' | 'history_actions'
    >('history_alarms');

    const [siteCall, siteQuery] = useLazyQuery<
      { site: Query['site'] },
      QuerySiteArgs
    >(siteGql);

    const site = siteQuery.data?.site;

    useEffect(() => {
      if (id) {
        siteCall({ variables: { siteId: id } });
      }
    }, [id]);

    return (
      <Drawer
        onClose={onClose}
        isOpen={isOpen}
        title={site?.terminalId}
        subTitle="Détail horodateur"
      >
        <Zone
          zones={[['filter'], ['description']]}
          rows={['min-content', 'min-content']}
          columns={['1fr']}
          gapY={32}
        >
          <ZoneArea area="filter">
            <TabGroup>
              <Tab
                disabled={true}
                selected={menu === 'info'}
                onClick={() => setMenu('info')}
              >
                Info
              </Tab>
              <Tab
                selected={menu === 'history_alarms'}
                onClick={() => setMenu('history_alarms')}
              >
                Alarmes en cours
              </Tab>

              <Tab
                disabled={true}
                selected={menu === 'history_actions'}
                onClick={() => setMenu('history_actions')}
              >
                Historique des actions
              </Tab>
            </TabGroup>
          </ZoneArea>

          <ZoneArea area="description">
            <Col>
              {(site?.monitoring?.currentAlarms &&
              Array.isArray(site?.monitoring?.currentAlarms) &&
              site?.monitoring?.currentAlarms?.length > 0
                ? [...site?.monitoring?.currentAlarms]
                    .sort(
                      (a: any, b: any) =>
                        (a.startDate ? new Date(a.startDate).getTime() : 0) -
                        (b.startDate ? new Date(b.startDate).getTime() : 0),
                    )
                    .map((value) => {
                      const alarm = value?.alarm as Alarm;
                      const startDate = value?.startDate
                        ? DateTime.fromISO(value?.startDate).setZone(site?.client?.timezone || "Europe/Paris").toISO()
                        : undefined;

                      return {
                        label: alarmsMapper[alarm] || alarm,
                        value: startDate
                          ? `depuis le ${baseFormattedDate({
                              date: startDate,
                              lang,
                            })}`
                          : null,
                        key: alarm,
                      };
                    })
                : []
              ).map(({ value, label, key }) => {
                return (
                  <Row
                    width="max-content"
                    alignItems="center"
                    justifyContent="space-between"
                    key={key}
                  >
                    <Text
                      variant="body"
                      whiteSpace="nowrap"
                      color="deep_indigo"
                    >
                      {label}
                    </Text>
                    <Text
                      variant="body"
                      whiteSpace="nowrap"
                      color="deep_indigo_70"
                    >
                      {value}
                    </Text>
                  </Row>
                );
              })}
            </Col>
          </ZoneArea>
        </Zone>
      </Drawer>
    );
  },
);
